import axios, {
  AxiosInstance,
  InternalAxiosRequestConfig,
  AxiosResponse,
} from "axios";

const API_BASE_URL =
  process.env.API_BASE_URL || "https://api.crays.co.za/admin";
const API_TIMEOUT = process.env.API_TIMEOUT
  ? parseInt(process.env.API_TIMEOUT, 10)
  : 10000;

// Append /v1 + route to API_BASE_URL (which already includes /admin)
const VERSION_PREFIX = "/v1";

// Auth tokens live in httpOnly cookies (set by the backend) rather than
// localStorage/JS-readable state — withCredentials sends them automatically
// on every same-site request, and no Authorization header is ever attached.
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

// Prepend version prefix to all request URLs
apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (config.url && !config.url.startsWith(VERSION_PREFIX)) {
    config.url =
      VERSION_PREFIX +
      (config.url.startsWith("/") ? config.url : "/" + config.url);
  }
  return config;
});

let refreshPromise: Promise<boolean> | null = null;

async function doRefresh(): Promise<boolean> {
  try {
    await axios.post(
      `${apiClient.defaults.baseURL}${VERSION_PREFIX}/auth/refresh`,
      null,
      { withCredentials: true },
    );
    return true;
  } catch {
    return false;
  } finally {
    refreshPromise = null;
  }
}

function refreshSession(): Promise<boolean> {
  if (!refreshPromise) refreshPromise = doRefresh();
  return refreshPromise;
}

apiClient.interceptors.response.use(
  (r: AxiosResponse) => r,
  async (error) => {
    const original = error.config;
    if (error.response?.status === 401 && !original._retry) {
      original._retry = true;

      const refreshed = await refreshSession();
      if (refreshed) {
        return apiClient(original);
      }

      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
      return Promise.reject(error);
    }
    return Promise.reject(error);
  },
);

export default apiClient;
